import { useTranslations } from '@vocab/react';
import { Text, IconChevron, Inline, Box } from 'braid-design-system';
import { type FC, Fragment } from 'react';
import translations from '../.vocab';
import RoleRequirementIcon from 'src/components/list/JobApplicationCard/RoleRequirementsView/RoleRequirement/RoleRequirementIcon';
import {
  STATUS_CERTIFIED,
  STATUS_CUSTOM,
  STATUS_MATCH,
} from 'src/constants/roleRequirementStatus';
import { CertsyToolTip } from 'src/components/common/Certsy/CertsyTooltip/CertsyTooltip';
import { CertsyVerifiedBadgeTooltipContent } from 'src/components/list/JobApplicationCard/RoleRequirementsView/RoleRequirement/RoleRequirementCredentialTooltip/CertsyVerifiedBadgeTooltipContent';
import * as styles from './RoleRequirementDisclosure.css';
import { RoleRequirement, type RoleRequirementProps } from './RoleRequirement';

interface RoleRequirementsDisclosureProps {
  roleRequirements: Array<RoleRequirementProps['roleRequirement']>;
  jobApplicationId: string;
  isMobile: boolean;
  isExpanded: boolean;
  toggleIsExpanded: () => void;
}

export const RoleRequirementsDisclosure: FC<
  RoleRequirementsDisclosureProps
> = ({
  roleRequirements,
  jobApplicationId,
  isMobile,
  isExpanded,
  toggleIsExpanded,
}) => {
  const { t } = useTranslations(translations);

  const {
    customQuestionsCount,
    libraryQuestionsCount,
    matchingLibraryQuestionsCount,
    verifiedAnswersCount,
  } = roleRequirements.reduce(
    (counts, { status }) => {
      if (status === STATUS_CUSTOM) {
        counts.customQuestionsCount = counts.customQuestionsCount + 1;
      } else {
        counts.libraryQuestionsCount = counts.libraryQuestionsCount + 1;
      }

      if (status === STATUS_CERTIFIED) {
        counts.verifiedAnswersCount = counts.verifiedAnswersCount + 1;
        counts.matchingLibraryQuestionsCount =
          counts.matchingLibraryQuestionsCount + 1;
      } else if (status === STATUS_MATCH) {
        counts.matchingLibraryQuestionsCount =
          counts.matchingLibraryQuestionsCount + 1;
      }
      return counts;
    },
    {
      matchingLibraryQuestionsCount: 0,
      libraryQuestionsCount: 0,
      verifiedAnswersCount: 0,
      customQuestionsCount: 0,
    },
  );

  const hasOnlyCustomQuestions =
    libraryQuestionsCount === 0 && customQuestionsCount > 0;

  const expandLabel = hasOnlyCustomQuestions
    ? t('Show questionCount custom questions', {
        questionCount: customQuestionsCount,
      })
    : t('Show screening questions (matchingQuestions/totalQuestions matched)', {
        matchingQuestions: String(matchingLibraryQuestionsCount),
        totalQuestions: String(libraryQuestionsCount),
      });

  const collapseLabel = hasOnlyCustomQuestions
    ? t('Hide questionCount custom questions', {
        questionCount: customQuestionsCount,
      })
    : t('Hide screening questions (matchingQuestions/totalQuestions matched)', {
        matchingQuestions: String(matchingLibraryQuestionsCount),
        totalQuestions: String(libraryQuestionsCount),
      });

  const chevron = (
    <Text size="small">
      <IconChevron direction={isExpanded ? 'up' : 'down'} alignY="lowercase" />
    </Text>
  );

  return (
    <>
      {/* This Inline needs to be here to limit the underline to the content of Box, otherwise, the underline would go til the end of the line */}
      <Inline space="none">
        <Box
          role="button"
          tabIndex={0}
          cursor="pointer"
          onClick={toggleIsExpanded}
          className={
            verifiedAnswersCount > 0
              ? styles.underlineOnHoverWithCertsy
              : styles.underlineOnHover
          }
        >
          <Inline space="xsmall" alignY="center">
            <Text size="small" tone="neutral">
              {isExpanded ? collapseLabel : expandLabel}
            </Text>
            {verifiedAnswersCount > 0 && (
              <Inline space="xxsmall" alignY="center">
                <CertsyToolTip
                  tooltipContent={<CertsyVerifiedBadgeTooltipContent />}
                  offset={[0, 0]}
                  placement="bottom-start"
                  fallbackPlacements={['top-start', 'bottom-start']}
                >
                  <RoleRequirementIcon status={STATUS_CERTIFIED} />
                </CertsyToolTip>
                <Text size="small">
                  {t('verifiedCount verified', {
                    verifiedCount: String(verifiedAnswersCount),
                  })}
                </Text>
                {chevron}
              </Inline>
            )}
            {verifiedAnswersCount === 0 && chevron}
          </Inline>
        </Box>
      </Inline>

      {isExpanded &&
        roleRequirements.map((rr, index) => (
          <Fragment key={rr.question}>
            {/* FIXME:
             * - `marginTop: 16px` is not in Braid but it's a workaround because we are using the legacy RoleRequirements
             * - We use `marginTop: 12px` for the first element because there's a custom padding for the disclosure element which makes the first element to be too far from the disclosure element
             */}
            {index === 0 ? (
              <div style={{ marginTop: '12px' }} />
            ) : (
              <div style={{ marginTop: '16px' }} />
            )}
            <RoleRequirement
              key={rr.question}
              jobApplicationId={jobApplicationId}
              isMobile={isMobile}
              roleRequirement={rr}
              hasOnlyCustomQuestions={hasOnlyCustomQuestions}
            />
          </Fragment>
        ))}
    </>
  );
};
